<template>
    <div>
        <div class="charts-container">
            <div class="chart-container">
                <div class="title-grid">
                    <h3>{{ $gettext('Revenue') }}</h3>
                    <CustomerPieChart v-show="!loading" :valueList="revenueList"></CustomerPieChart>
                    <b-skeleton type="avatar" v-if="loading" class="skeleton-size"></b-skeleton>
                </div>
            </div>
            <div class="chart-container">
                <div class="title-grid">
                    <h3>{{ $gettext('Cost') }}</h3>
                    <CustomerPieChart v-show="!loading" :valueList="costList"></CustomerPieChart>
                    <b-skeleton type="avatar" v-if="loading" class="skeleton-size"></b-skeleton>
                </div>
            </div>
        </div>

        <b-table
            :fields="fields"
            :items="items"
            :class="['gk-table gk-table--responsive gk-table--striped gk-table--headed']"
            :busy="loading"
            ref="report_table"
            :tbody-tr-class="rowClass"
            show-empty
            table-busy>
            <template #cell(name)="row">
                <span>{{ row.value }}</span>
                &nbsp;
                <font-awesome-icon v-if="row.item.is_archived" icon="fa-solid fa-folder" />
            </template>
            <template #head(name)>
                <translate>Customer</translate>
                &nbsp;
                <GekkoButton
                    @click="sortByName"
                    :icon="sortNameDesc ? 'fa fa-arrow-down-z-a' : 'fa fa-arrow-up-z-a'"
                    class="p-0 sort-icon-class"
                    variant="link"
                />
            </template>
            <template #cell(revenue)="row">{{ formatNumber(row.value) }}</template>
            <template #cell(cost)="row">{{ formatNumber(row.value) }}</template>
            <template #cell(margin)="row">{{ formatNumber(row.value) }}</template>
        </b-table>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import InvoiceService from '@/store/services/invoice.service.js'
import CostService from '@/store/services/cost.service.js'
import CustomerPieChart from '@/components/customer_insight/CustomerPieChart'
import GekkoButton from '@/components/GekkoButton.vue'

export default {
    name: "MarginTable",
    components: {CustomerPieChart, GekkoButton},
    props: {
        customers: {
            type: Array,
            default: () => []
        },
        filters: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            items: [],
            fields: [
                { key: 'name', label: this.$gettext('Customer')},
                { key: 'revenue', label: this.$gettext('Revenue'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'cost', label: this.$gettext('Cost'), thClass: 'th-class', tdClass: 'td-class', sortable: true},
                { key: 'margin', label: this.$gettext('Margin'), thClass: 'th-class', tdClass: 'td-class', sortable: true}
            ],
            sortNameDesc: false,
            loading_customers: true,
            loading_filters: true
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
        revenueList() {
            return this.items.filter((item) => item.revenue > 0).map((item) => { return {'label': item.name, 'value': item.revenue}})
        },
        costList() {
            return this.items.filter((item) => item.cost > 0).map((item) => { return {'label': item.name, 'value': item.cost}})
        },
        loading() {
            return this.loading_customers || this.loading_filters
        }
    },
    watch: {
        customers() {
            this.loading_customers = true
            this.setReportData('customers')
        },
        filters() {
            this.loading_filters = true
            this.setReportData('filters')
        }
    },
    methods: {
        setReportData(loading_type) {
            if (this.$store.getters['getIsPremium']) {
                if (this.customers.length)
                    this.setRealData(loading_type)
            } else
                this.setFakeData(loading_type)
        },
        setRealData(loading_type) {
            let temp_data = JSON.parse(JSON.stringify(this.customers))
            temp_data.unshift({'id': null, 'name': this.$gettext('(no customer)')})

            let filters_costs = {
                'start_date': this.filters.start_date,
                'end_date': this.filters.end_date,
                'group_by': 'customer',
                'owner_company': this.getCompanyId,
                'tax_exists': true,
                'tax_exempt': true,
                'btw_reversed_charge': true
            }

            let filters_invoices = {
                'start_date': this.filters.start_date,
                'end_date': this.filters.end_date,
                'group_by' : 'recipient_customer',
                'sender_company': this.getCompanyId,
                'status': 'p,s'
            }

            let promises = []
            promises.push(CostService.totalNet(filters_costs))
            promises.push(InvoiceService.totalNet(filters_invoices))
            
            Promise.all(promises).then((responses) => {
                this.items = []
                temp_data = this.mergeTwoLists(temp_data, responses[0].data, 'id', 'customer', 'total_expenses', 'total_net')

                for(let x = 0; x < responses[1].data.length; x++) {
                    if(this.getCompanyId === responses[1].data[x].recipient_customer) {
                        responses[1].data[x].recipient_customer = null
                    }
                }

                temp_data = this.mergeTwoLists(temp_data, responses[1].data, 'id', 'recipient_customer', 'total_revenue', 'total')
                
                for(let i = 0; i < temp_data.length; i++) {
                    if(isNaN(temp_data[i].total_revenue) || temp_data[i].total_revenue == null)
                        temp_data[i].total_revenue = 0
                    
                    if(isNaN(temp_data[i].total_expenses) || temp_data[i].total_expenses == null )
                        temp_data[i].total_expenses = 0
                    
                    if (this.isBiggerThanZero(temp_data[i])) {
                        this.items.push({
                            'id': temp_data[i].id,
                            'name' : temp_data[i].name,
                            'revenue': temp_data[i].total_revenue,
                            'cost': temp_data[i].total_expenses,
                            'margin': temp_data[i].total_revenue - temp_data[i].total_expenses,
                            'is_archived': temp_data[i].is_archived
                        })
                    }
                }
                
                this.loading_customers = false
                this.loading_filters = false
            })
        },
        setFakeData(loading_type) {
            this.items = []

            this.items.push({
                "name": this.$gettext("(no customer)"),
                "revenue": (150),
                "cost": (40),
                "margin": (150 - 40)
            })

            for(let i = 1; i <= 4; i++) {
                this.items.push({
                    "name": `${this.$gettext("fake customer")} ${i}`,
                    "revenue": (300 * i),
                    "cost": (200 * i),
                    "margin": ((300 * i) - (200*1))
                })
            }

            if (loading_type === 'customers')
                this.loading_customers = false
            if (loading_type === 'filters')
                this.loading_filters = false
        },
        mergeTwoLists(list1, list2, ifProp1, ifProp2, prop1, prop2) {
            let finalList = list1.map((x) => x)
            for(let i in finalList) {
                for (let j in list2) {
                    if (finalList[i][ifProp1] == list2[j][ifProp2]) {
                        finalList[i][prop1] = list2[j][prop2]
                    }
                }
            }
            return finalList
        },
        formatNumber(num) {
            num = parseInt(num.toFixed(0))
            let str = num.toLocaleString('en-US')
            return `€ ${str}`
        },
        isBiggerThanZero(item) {
            return item.total_revenue != 0 || item.total_expenses != 0 || (item.total_revenue - item.total_expenses) != 0
        },
        sortByName() {
            if (this.sortNameDesc) {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase()
                    const nameB = b.name.toUpperCase()

                    if (nameA < nameB)
                        return -1
                    
                    if (nameA > nameB)
                        return 1
                    
                    return 0
                })
            } else {
                this.items.sort((a, b) => {
                    const nameA = a.name.toUpperCase()
                    const nameB = b.name.toUpperCase()

                    if (nameA > nameB)
                        return -1

                    if (nameA < nameB)
                        return 1
                    
                    return 0
                })
            }

            this.sortNameDesc = !this.sortNameDesc
        },
        rowClass(item) {
            if (item && item.is_archived)
                return "archived-row"
            return ""
        }
    }
}
</script>

<style scoped>
.charts-container {
    display: flex;
    flex-direction: row;
    padding-top: 1rem;
    padding-bottom: 1rem;
    overflow: auto;
}

.chart-container {
    flex-grow: 1;
    display: flex;
    justify-content: center;
}

.title-grid {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.skeleton-size {
    min-width: 180px;
    width: 300px;
    height: 300px;
}
</style>

<style>
.th-class {
    text-align: right;
}

.td-class {
    text-align: right;
}

.sort-icon-class:hover {
    color: #3f444d;
}

.archived-row {
    color: #bbb;
}
</style>